import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert, ListGroup, Tabs, Tab, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { colors } from '../../brand-colors';

function BookEnhance() {
  const navigate = useNavigate();
  const { bookId } = useParams();

  // State
  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState(null);
  const [error, setError] = useState('');

  // Competitor research state
  const [searchingCompetitors, setSearchingCompetitors] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const [marketInsights, setMarketInsights] = useState(null);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);

  // Chapter enhancement state
  const [researchingChapter, setResearchingChapter] = useState(null);
  const [chapterEnhancements, setChapterEnhancements] = useState({});
  const [selectedEnhancements, setSelectedEnhancements] = useState({});

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    loadBook();
  }, [bookId]);

  const loadBook = async () => {
    try {
      // First get the book details
      const res = await fetch(`/api/authority-engineer/book/${bookId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setBook(data.project);
          // Parse existing enhancements if any
          if (data.project.competitor_books) {
            try {
              const parsed = JSON.parse(data.project.competitor_books);
              setCompetitors(parsed);
              setSelectedCompetitors(parsed.map((_, i) => i));
            } catch (e) {}
          }
          if (data.project.chapter_enhancements) {
            try {
              const parsed = JSON.parse(data.project.chapter_enhancements);
              setChapterEnhancements(parsed);
              // Select all existing enhancements
              const selected = {};
              Object.keys(parsed).forEach(ch => {
                selected[ch] = {
                  statistics: parsed[ch].statistics?.map((_, i) => i) || [],
                  case_studies: parsed[ch].case_studies?.map((_, i) => i) || [],
                  expert_quotes: parsed[ch].expert_quotes?.map((_, i) => i) || []
                };
              });
              setSelectedEnhancements(selected);
            } catch (e) {}
          }
        } else {
          setError('Book not found');
        }
      } else {
        setError('Failed to load book');
      }
    } catch (err) {
      setError('Error loading book: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchCompetitors = async () => {
    setSearchingCompetitors(true);
    setError('');
    try {
      const res = await fetch(`/api/authority-engineer/book/${bookId}/enhance/competitors`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setCompetitors(data.competitors || []);
        setMarketInsights(data.market_insights);
        // Select all by default
        setSelectedCompetitors(data.competitors?.map((_, i) => i) || []);
      } else {
        setError(data.error || 'Failed to search competitors');
      }
    } catch (err) {
      setError('Error searching: ' + err.message);
    } finally {
      setSearchingCompetitors(false);
    }
  };

  const researchChapter = async (chapterNum) => {
    setResearchingChapter(chapterNum);
    setError('');
    try {
      const res = await fetch(`/api/authority-engineer/book/${bookId}/enhance/chapter/${chapterNum}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setChapterEnhancements(prev => ({
          ...prev,
          [chapterNum]: {
            statistics: data.statistics || [],
            case_studies: data.case_studies || [],
            expert_quotes: data.expert_quotes || [],
            recommended_resources: data.recommended_resources || []
          }
        }));
        // Select all by default
        setSelectedEnhancements(prev => ({
          ...prev,
          [chapterNum]: {
            statistics: data.statistics?.map((_, i) => i) || [],
            case_studies: data.case_studies?.map((_, i) => i) || [],
            expert_quotes: data.expert_quotes?.map((_, i) => i) || []
          }
        }));
      } else {
        setError(data.error || 'Failed to research chapter');
      }
    } catch (err) {
      setError('Error researching: ' + err.message);
    } finally {
      setResearchingChapter(null);
    }
  };

  const toggleCompetitor = (index) => {
    setSelectedCompetitors(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const toggleEnhancement = (chapterNum, type, index) => {
    setSelectedEnhancements(prev => {
      const chapter = prev[chapterNum] || { statistics: [], case_studies: [], expert_quotes: [] };
      const list = chapter[type] || [];
      if (list.includes(index)) {
        return {
          ...prev,
          [chapterNum]: {
            ...chapter,
            [type]: list.filter(i => i !== index)
          }
        };
      } else {
        return {
          ...prev,
          [chapterNum]: {
            ...chapter,
            [type]: [...list, index]
          }
        };
      }
    });
  };

  const saveEnhancements = async () => {
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    try {
      // Filter to only selected items
      const selectedCompetitorBooks = selectedCompetitors.map(i => competitors[i]);

      const selectedChapterEnhancements = {};
      Object.keys(chapterEnhancements).forEach(ch => {
        const chData = chapterEnhancements[ch];
        const sel = selectedEnhancements[ch] || { statistics: [], case_studies: [], expert_quotes: [] };
        selectedChapterEnhancements[ch] = {
          statistics: sel.statistics?.map(i => chData.statistics[i]).filter(Boolean) || [],
          case_studies: sel.case_studies?.map(i => chData.case_studies[i]).filter(Boolean) || [],
          expert_quotes: sel.expert_quotes?.map(i => chData.expert_quotes[i]).filter(Boolean) || [],
          recommended_resources: chData.recommended_resources || []
        };
      });

      const res = await fetch(`/api/authority-engineer/book/${bookId}/enhance/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          competitor_books: selectedCompetitorBooks,
          chapter_enhancements: selectedChapterEnhancements
        })
      });
      const data = await res.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading book...</p>
      </Container>
    );
  }

  if (!book) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          Book not found. <Button variant="link" onClick={() => navigate('/command-center/authority-engineer/book')}>Back to books</Button>
        </Alert>
      </Container>
    );
  }

  const chapters = book.chapter_outline ? JSON.parse(book.chapter_outline) : [];

  return (
    <Container fluid className="py-4 px-4">
      <Card className="shadow mb-4">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">Enhance Your Book</h4>
              <small>{book.title}</small>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="success"
                onClick={saveEnhancements}
                disabled={saving}
              >
                {saving ? <><Spinner size="sm" className="me-2" /> Saving...</> : 'Save Selected'}
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate('/command-center/authority-engineer/book')}
              >
                Back
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {saveSuccess && <Alert variant="success">Enhancements saved! They'll be included in your book kit export.</Alert>}

          <Tabs defaultActiveKey="competitors" className="mb-4">
            {/* COMPETITOR RESEARCH TAB */}
            <Tab eventKey="competitors" title="Competitor Research">
              <Card className="border-0 bg-light p-3 mb-4">
                <p className="mb-3">
                  Search for competing books on Amazon to understand your market positioning and differentiate your book.
                </p>
                <Button
                  variant="primary"
                  onClick={searchCompetitors}
                  disabled={searchingCompetitors}
                >
                  {searchingCompetitors ? (
                    <><Spinner size="sm" className="me-2" /> Searching Amazon...</>
                  ) : competitors.length > 0 ? 'Search Again' : 'Search for Competitors'}
                </Button>
              </Card>

              {competitors.length > 0 && (
                <>
                  <h5 className="mb-3">Competing Books</h5>
                  <p className="text-muted mb-3">Check the books you want to reference in your kit:</p>
                  <ListGroup className="mb-4">
                    {competitors.map((comp, idx) => (
                      <ListGroup.Item
                        key={idx}
                        className="d-flex align-items-start"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleCompetitor(idx)}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={selectedCompetitors.includes(idx)}
                          onChange={() => toggleCompetitor(idx)}
                          className="me-3 mt-1"
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between">
                            <strong>{comp.title}</strong>
                            {comp.estimated_popularity && (
                              <Badge bg={comp.estimated_popularity === 'high' ? 'success' : comp.estimated_popularity === 'medium' ? 'warning' : 'secondary'}>
                                {comp.estimated_popularity} popularity
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted small">by {comp.author}</div>
                          {comp.unique_angle && <div className="mt-1 small">{comp.unique_angle}</div>}
                          {comp.amazon_url && (
                            <a
                              href={comp.amazon_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View on Amazon
                            </a>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>

                  {marketInsights && (
                    <Card className="border-info">
                      <Card.Header className="bg-info text-white">Market Insights</Card.Header>
                      <Card.Body>
                        {marketInsights.common_themes?.length > 0 && (
                          <p><strong>Common Themes:</strong> {marketInsights.common_themes.join(', ')}</p>
                        )}
                        {marketInsights.gaps_in_market?.length > 0 && (
                          <p><strong>Gaps to Exploit:</strong> {marketInsights.gaps_in_market.join(', ')}</p>
                        )}
                        {marketInsights.pricing_range && (
                          <p><strong>Typical Pricing:</strong> {marketInsights.pricing_range}</p>
                        )}
                        {marketInsights.audience_overlap && (
                          <p><strong>Audience:</strong> {marketInsights.audience_overlap}</p>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </Tab>

            {/* CHAPTER RESEARCH TAB */}
            <Tab eventKey="chapters" title="Chapter Enhancements">
              <Card className="border-0 bg-light p-3 mb-4">
                <p className="mb-0">
                  Research statistics, case studies, and expert quotes to strengthen each chapter.
                  Select which ones to include in your book kit.
                </p>
              </Card>

              <Row>
                {chapters.map((chapter, idx) => {
                  const chapterNum = idx + 1;
                  const hasEnhancements = chapterEnhancements[chapterNum];
                  const isResearching = researchingChapter === chapterNum;

                  return (
                    <Col md={6} lg={4} key={idx} className="mb-4">
                      <Card className={hasEnhancements ? 'border-success' : ''}>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                          <span>Ch. {chapterNum}: {chapter.title?.substring(0, 25)}{chapter.title?.length > 25 ? '...' : ''}</span>
                          {hasEnhancements && <Badge bg="success">Researched</Badge>}
                        </Card.Header>
                        <Card.Body>
                          <Button
                            variant={hasEnhancements ? 'outline-primary' : 'primary'}
                            size="sm"
                            className="w-100"
                            onClick={() => researchChapter(chapterNum)}
                            disabled={isResearching}
                          >
                            {isResearching ? (
                              <><Spinner size="sm" className="me-2" /> Researching...</>
                            ) : hasEnhancements ? 'Research Again' : 'Research Stats & Examples'}
                          </Button>

                          {hasEnhancements && (
                            <div className="mt-3 small">
                              <div className="text-muted">
                                {hasEnhancements.statistics?.length || 0} stats, {' '}
                                {hasEnhancements.case_studies?.length || 0} cases, {' '}
                                {hasEnhancements.expert_quotes?.length || 0} quotes
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {/* Show detailed enhancements for researched chapters */}
              {Object.keys(chapterEnhancements).length > 0 && (
                <Card className="mt-4">
                  <Card.Header>Select Enhancements to Include</Card.Header>
                  <Card.Body>
                    {Object.entries(chapterEnhancements).map(([chapterNum, data]) => {
                      const chapter = chapters[parseInt(chapterNum) - 1];
                      const selected = selectedEnhancements[chapterNum] || { statistics: [], case_studies: [], expert_quotes: [] };

                      return (
                        <div key={chapterNum} className="mb-4 pb-4 border-bottom">
                          <h5>Chapter {chapterNum}: {chapter?.title}</h5>

                          {data.statistics?.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-primary">Statistics</h6>
                              {data.statistics.map((stat, i) => (
                                <div
                                  key={i}
                                  className="d-flex align-items-start mb-2 p-2 bg-light rounded"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => toggleEnhancement(chapterNum, 'statistics', i)}
                                >
                                  <Form.Check
                                    type="checkbox"
                                    checked={selected.statistics?.includes(i)}
                                    onChange={() => toggleEnhancement(chapterNum, 'statistics', i)}
                                    className="me-2"
                                  />
                                  <div>
                                    <div><strong>{stat.stat}</strong></div>
                                    <div className="small text-muted">
                                      Source: {stat.source} ({stat.year})
                                      {stat.source_url && (
                                        <> - <a href={stat.source_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Link</a></>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {data.case_studies?.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-success">Case Studies</h6>
                              {data.case_studies.map((cs, i) => (
                                <div
                                  key={i}
                                  className="d-flex align-items-start mb-2 p-2 bg-light rounded"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => toggleEnhancement(chapterNum, 'case_studies', i)}
                                >
                                  <Form.Check
                                    type="checkbox"
                                    checked={selected.case_studies?.includes(i)}
                                    onChange={() => toggleEnhancement(chapterNum, 'case_studies', i)}
                                    className="me-2"
                                  />
                                  <div>
                                    <div><strong>{cs.company_or_subject}</strong></div>
                                    <div className="small">{cs.summary}</div>
                                    <div className="small text-muted">
                                      Key lesson: {cs.key_lesson}
                                      {cs.source_url && (
                                        <> - <a href={cs.source_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Source</a></>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {data.expert_quotes?.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-info">Expert Quotes</h6>
                              {data.expert_quotes.map((quote, i) => (
                                <div
                                  key={i}
                                  className="d-flex align-items-start mb-2 p-2 bg-light rounded"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => toggleEnhancement(chapterNum, 'expert_quotes', i)}
                                >
                                  <Form.Check
                                    type="checkbox"
                                    checked={selected.expert_quotes?.includes(i)}
                                    onChange={() => toggleEnhancement(chapterNum, 'expert_quotes', i)}
                                    className="me-2"
                                  />
                                  <div>
                                    <div className="fst-italic">"{quote.quote}"</div>
                                    <div className="small text-muted">
                                      - {quote.attribution}
                                      {quote.source_url && (
                                        <> - <a href={quote.source_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Source</a></>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default BookEnhance;
